import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Grid, TextField, IconButton,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  OpenInNew as ExternalLinkIcon,
  TrendingUp as UsageIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { billingApi, tenantAdminApi } from '../api/services';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

function formatPrice(pence: number, currency?: string): string {
  const amount = pence / 100;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
  } catch {
    return `${currency || 'GBP'} ${amount.toFixed(2)}`;
  }
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

interface BillingStatus {
  status: string;
  plan?: string;
  seatCount?: number;
  pricePerSeat?: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  stripeCustomerId?: string;
  message?: string;
}

interface UsageData {
  year: number;
  month: number;
  totals: {
    planGenerations: number;
    workItemsCreated: number;
    transcriptsProcessed: number;
    imageAnalyses: number;
    aiTokensUsed: number;
  };
  limits: {
    monthlyAiTokenLimit?: number;
    monthlyTranscriptLimit?: number;
  };
}

function UsageBar({ label, current, limit }: { label: string; current: number; limit?: number }) {
  const percent = limit && limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isOverLimit = percent >= 100;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: isOverLimit ? COLORS.error : isHigh ? COLORS.warning : COLORS.textSecondary }}>
          {formatNumber(current)}{limit ? ` / ${formatNumber(limit)}` : ''}
        </Typography>
      </Stack>
      {limit != null && limit > 0 && (
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': {
              bgcolor: isOverLimit ? COLORS.error : isHigh ? COLORS.warning : COLORS.primary,
              borderRadius: 3,
            },
          }}
        />
      )}
    </Box>
  );
}

export default function TenantBillingPage() {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [billingEmailSaving, setBillingEmailSaving] = useState(false);
  const [billingEmailSuccess, setBillingEmailSuccess] = useState<string | null>(null);

  // Seat management state
  const [tenantInfo, setTenantInfo] = useState<{
    maxExecutiveSeats: number;
    currentExecutives: number;
    maxEaSeats: number;
    monthlyTranscriptLimit: number;
  } | null>(null);
  const [newSeatCount, setNewSeatCount] = useState<number>(1);
  const [seatUpdating, setSeatUpdating] = useState(false);
  const [seatSuccess, setSeatSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusResult, usageResult, tenantResult] = await Promise.allSettled([
        billingApi.getStatus(),
        tenantAdminApi.getUsage(),
        tenantAdminApi.getTenant(),
      ]);

      if (statusResult.status === 'fulfilled') {
        setBillingStatus(statusResult.value);
      }
      if (usageResult.status === 'fulfilled') {
        setUsage(usageResult.value);
      }
      if (tenantResult.status === 'fulfilled') {
        const t = tenantResult.value;
        if (t?.billingEmail) setBillingEmail(t.billingEmail);
        if (t) {
          setTenantInfo({
            maxExecutiveSeats: t.maxExecutiveSeats,
            currentExecutives: t.currentExecutives,
            maxEaSeats: t.maxEaSeats,
            monthlyTranscriptLimit: t.monthlyTranscriptLimit,
          });
          setNewSeatCount(t.maxExecutiveSeats);
        }
      }

      if (statusResult.status === 'rejected' && usageResult.status === 'rejected') {
        setError('Failed to load billing information.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const result = await billingApi.getPortalUrl();
      if (result.portalUrl) {
        window.open(result.portalUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpdateBillingEmail = async () => {
    if (!billingEmail.trim()) return;
    setBillingEmailSaving(true);
    setBillingEmailSuccess(null);
    try {
      await tenantAdminApi.updateBillingEmail(billingEmail.trim());
      setBillingEmailSuccess('Billing email updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update billing email.');
    } finally {
      setBillingEmailSaving(false);
    }
  };

  const handleUpdateSeats = async () => {
    if (newSeatCount < 1) return;
    setSeatUpdating(true);
    setSeatSuccess(null);
    setError(null);
    try {
      const result = await tenantAdminApi.updateSeats(newSeatCount);
      setSeatSuccess(result.message || 'Seats updated successfully.');
      if (tenantInfo) {
        setTenantInfo({
          ...tenantInfo,
          maxExecutiveSeats: result.executiveSeats,
          maxEaSeats: result.eaSeats,
          monthlyTranscriptLimit: result.monthlyTranscriptLimit,
        });
      }
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update seats.');
    } finally {
      setSeatUpdating(false);
    }
  };

  const seatDifference = tenantInfo ? newSeatCount - tenantInfo.maxExecutiveSeats : 0;
  const estimatedMonthlyCostChange = billingStatus?.pricePerSeat
    ? (seatDifference * billingStatus.pricePerSeat) / 100
    : null;

  const statusColor = (status?: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Trialing': return 'info';
      case 'PastDue': return 'warning';
      case 'Cancelled': return 'error';
      case 'Paused': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PaymentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Billing & Subscription</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your plan, view usage, and update payment details
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {/* Current Plan Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
                Current Plan
              </Typography>

              {billingStatus?.status === 'NoBilling' ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                    No billing configured for this tenant.
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    Contact your platform administrator to set up a subscription.
                  </Typography>
                </Box>
              ) : billingStatus ? (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ color: COLORS.primary, fontWeight: 700 }}>
                      {billingStatus.plan || 'Unknown Plan'}
                    </Typography>
                    <Chip
                      label={billingStatus.status}
                      size="small"
                      color={statusColor(billingStatus.status) as any}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {billingStatus.pricePerSeat != null && billingStatus.pricePerSeat > 0 && (
                    <Box>
                      <Typography variant="body1" sx={{ color: COLORS.textPrimary }}>
                        {formatPrice(billingStatus.pricePerSeat)} per seat
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        {billingStatus.seatCount || 1} seat{(billingStatus.seatCount || 1) !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  {billingStatus.currentPeriodEnd && (
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Current Period
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textPrimary, mt: 0.5 }}>
                        {billingStatus.currentPeriodStart
                          ? new Date(billingStatus.currentPeriodStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '---'
                        }
                        {' to '}
                        {new Date(billingStatus.currentPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  )}

                  {billingStatus.stripeCustomerId && (
                    <Button
                      variant="contained"
                      startIcon={<ExternalLinkIcon />}
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      sx={{ mt: 1, alignSelf: 'flex-start' }}
                    >
                      {portalLoading ? 'Opening...' : 'Manage Subscription'}
                    </Button>
                  )}
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  Unable to load billing status.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Usage This Month Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <UsageIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
                <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                  Usage This Month
                </Typography>
              </Stack>

              {usage ? (
                <Stack spacing={2}>
                  <UsageBar
                    label="Plan Generations"
                    current={usage.totals.planGenerations}
                  />
                  <UsageBar
                    label="Transcripts Processed"
                    current={usage.totals.transcriptsProcessed}
                    limit={usage.limits.monthlyTranscriptLimit}
                  />
                  {usage.limits.monthlyTranscriptLimit != null && tenantInfo && (
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, display: 'block', mt: -1 }}>
                      (Based on {tenantInfo.maxExecutiveSeats} executive{tenantInfo.maxExecutiveSeats !== 1 ? 's' : ''} x 1.5 transcripts/day x 30 days)
                    </Typography>
                  )}
                  <UsageBar
                    label="AI Tokens Used"
                    current={usage.totals.aiTokensUsed}
                    limit={usage.limits.monthlyAiTokenLimit}
                  />
                  <UsageBar
                    label="Image Analyses"
                    current={usage.totals.imageAnalyses}
                  />
                  <UsageBar
                    label="Work Items Created"
                    current={usage.totals.workItemsCreated}
                  />
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  No usage data available.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Manage Seats Card */}
      <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
            Manage Seats
          </Typography>
          {seatSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSeatSuccess(null)}>
              {seatSuccess}
            </Alert>
          )}
          {tenantInfo ? (
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                Current seats: {tenantInfo.maxExecutiveSeats} executive, {tenantInfo.maxEaSeats} EA
                {' '}({tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''})
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  size="small"
                  onClick={() => setNewSeatCount(Math.max(1, newSeatCount - 1))}
                  disabled={newSeatCount <= 1 || seatUpdating}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={newSeatCount}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= 500) setNewSeatCount(val);
                  }}
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 500, style: { textAlign: 'center', width: 60 } }}
                  disabled={seatUpdating}
                />
                <IconButton
                  size="small"
                  onClick={() => setNewSeatCount(Math.min(500, newSeatCount + 1))}
                  disabled={newSeatCount >= 500 || seatUpdating}
                  sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
                >
                  <AddIcon />
                </IconButton>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  executive seats
                </Typography>
              </Stack>
              {seatDifference !== 0 && (
                <Box>
                  {estimatedMonthlyCostChange !== null && (
                    <Typography variant="body2" sx={{ color: seatDifference > 0 ? COLORS.warning : COLORS.success }}>
                      {seatDifference > 0 ? 'Adding' : 'Removing'} {Math.abs(seatDifference)} seat{Math.abs(seatDifference) !== 1 ? 's' : ''}
                      {' = '}{seatDifference > 0 ? '+' : '-'}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Math.abs(estimatedMonthlyCostChange))}/mo
                    </Typography>
                  )}
                  {newSeatCount < (tenantInfo?.currentExecutives ?? 0) && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Cannot reduce below {tenantInfo.currentExecutives} seats — you have {tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''}.
                    </Alert>
                  )}
                </Box>
              )}
              <Button
                variant="contained"
                onClick={handleUpdateSeats}
                disabled={seatUpdating || seatDifference === 0 || newSeatCount < (tenantInfo?.currentExecutives ?? 0)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {seatUpdating ? 'Updating...' : 'Update Seats'}
              </Button>
            </Stack>
          ) : !loading ? (
            <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
              Unable to load seat information.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {/* Billing Email Card */}
      <Card sx={{ bgcolor: COLORS.bgPaper, mt: 2.5 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
            Billing Email
          </Typography>
          {billingEmailSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBillingEmailSuccess(null)}>
              {billingEmailSuccess}
            </Alert>
          )}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Billing Email"
              value={billingEmail}
              onChange={e => setBillingEmail(e.target.value)}
              helperText="This email receives all invoices and billing communications"
              type="email"
              size="small"
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateBillingEmail}
              disabled={billingEmailSaving || !billingEmail.trim()}
              sx={{ mt: '0 !important', minWidth: 160 }}
            >
              {billingEmailSaving ? 'Saving...' : 'Update Billing Email'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Info Footer */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ bgcolor: 'rgba(108, 155, 255, 0.08)', borderColor: 'rgba(108, 155, 255, 0.2)' }}>
          Need to update your payment method, view invoices, or change your plan? Use the <strong>Manage Subscription</strong> button above to access the Stripe Customer Portal.
        </Alert>
      </Box>
    </Box>
  );
}
